import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { validateEmailRealTime } from '../utils/validation';
import { getDashboardPath } from '../utils/roleUtils';
import TwoFactorVerification from '../components/TwoFactorVerification';

function Login({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ isValid: true, errors: [] });
  const [isEmailFormat, setIsEmailFormat] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const navigate = useNavigate();

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setCredentials({ ...credentials, username: value });
    
    // Check if input looks like an email
    const emailPattern = /@/;
    const looksLikeEmail = emailPattern.test(value);
    setIsEmailFormat(looksLikeEmail);
    
    // Validate if it looks like an email
    if (looksLikeEmail && value.trim()) {
      const validation = validateEmailRealTime(value);
      setEmailValidation(validation);
    } else {
      setEmailValidation({ isValid: true, errors: [] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(credentials);
      
      // Check if 2FA is required
      if (response.data.requires2FA) {
        setTwoFAData({
          email: response.data.email,
          userId: response.data.tempUserId
        });
        setShow2FA(true);
        toast.info('Verification code sent to your email');
      } else {
        // Normal login without 2FA
        const { token, user } = response.data;
        onLogin(token, user);
        
        if (user.mustChangePassword) {
          navigate('/change-password');
          toast.info('Please change your password');
        } else {
          const redirectPath = getDashboardPath(user.permissions, user.role);
          navigate(redirectPath);
          toast.success('Login successful');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = (data) => {
    const { token, user } = data;
    onLogin(token, user);
    
    if (user.mustChangePassword) {
      navigate('/change-password');
      toast.info('Please change your password');
    } else {
      const redirectPath = getDashboardPath(user.permissions, user.role);
      navigate(redirectPath);
      toast.success('Login successful');
    }
  };

  const handle2FACancel = () => {
    setShow2FA(false);
    setTwoFAData(null);
  };

  // Show 2FA verification if required
  if (show2FA && twoFAData) {
    return (
      <TwoFactorVerification
        email={twoFAData.email}
        userId={twoFAData.userId}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  return (
    <div className="login-container-split">
      {/* Left Side - Gradient with Branding */}
      <div className="login-gradient-side">
        <div className="login-gradient-content">
          <div className="login-brand-icon">
            <img 
              src="/assets/logo.png" 
              alt="OUK Logo" 
              className="gradient-logo"
            />
          </div>
        </div>
        <div className="login-gradient-shapes">
          <div className="gradient-shape shape-1"></div>
          <div className="gradient-shape shape-2"></div>
          <div className="gradient-shape shape-3"></div>
          <div className="gradient-shape shape-4"></div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="login-form-icon">
              <img 
                src="/assets/logo.png" 
                alt="OUK Logo" 
                className="login-logo"
              />
            </div>
            <h2 className="login-form-title">Login</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className={`login-input ${isEmailFormat && !emailValidation.isValid ? 'email-input-invalid' : isEmailFormat && emailValidation.isValid && credentials.username.trim() ? 'email-input-valid' : ''}`}
                  placeholder="Email address"
                  value={credentials.username}
                  onChange={handleUsernameChange}
                  required
                />
                {isEmailFormat && credentials.username.trim() && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '16px'
                  }}>
                    {emailValidation.isValid ? (
                      <span style={{ color: '#10b981' }}>✓</span>
                    ) : (
                      <span style={{ color: '#ef4444' }}>✗</span>
                    )}
                  </div>
                )}
              </div>
              {isEmailFormat && !emailValidation.isValid && credentials.username.trim() && (
                <div className="email-validation-errors" style={{ marginTop: '6px' }}>
                  {emailValidation.errors.map((error, index) => (
                    <div key={index} className="email-error-message">
                      <span style={{ color: '#ef4444', fontSize: '12px', marginRight: '4px' }}>⚠</span>
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>{error}</span>
                    </div>
                  ))}
                  {emailValidation.suggestion && (
                    <div className="email-suggestion">
                      <span style={{ color: '#f59e0b', fontSize: '12px', marginRight: '4px' }}>💡</span>
                      <span style={{ color: '#f59e0b', fontSize: '12px' }}>
                        Did you mean: <strong>{emailValidation.suggestion}</strong>?
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  className="login-input"
                  placeholder="Password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span>{showPassword ? '🙈' : '👁'}</span>
                </button>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="login-submit-btn" 
              disabled={loading || !credentials.username.trim() || !credentials.password.trim() || (isEmailFormat && !emailValidation.isValid)}
              style={{
                opacity: (loading || !credentials.username.trim() || !credentials.password.trim() || (isEmailFormat && !emailValidation.isValid)) ? 0.6 : 1
              }}
            >
              {loading ? 'Signing in...' : 'Enter'}
            </button>

            <div className="forgot-password">
              <Link to="/forgot-password" className="forgot-password-link">Forgot password?</Link>
            </div>
          </form>

          <div className="login-footer">
            <div className="login-footer-links">
              <a href="#" className="footer-link">Terms of Service</a>
              <a href="#" className="footer-link">Privacy Policy</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
